/* ==================================================================
   Return-to-here redirects
   ------------------------------------------------------------------
   A candidate who opens a shared job link and signs in has to come
   back to that job. The path travels as ?next=<encoded path>, which
   means it is attacker-controllable, so nothing uses it before it has
   been through safeNextPath().
   ================================================================== */

// Signing in and landing back on the sign-in page is a loop, not a return.
const NEVER_RETURN_TO = ['/login'];

/**
 * Validate a ?next= value as an in-app destination.
 *
 * Only same-origin paths survive. Anything that could leave the app — an
 * absolute URL, a protocol-relative "//evil.com", a backslash the browser
 * normalises into a slash — is rejected rather than sanitised, because a
 * half-repaired redirect target is worse than no redirect at all.
 *
 * @param {string|null} value raw ?next= parameter
 * @returns {string|null} a path safe to navigate to, or null
 */
export function safeNextPath(value) {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/')) return null;
  // "//host" is protocol-relative; "/\host" and "/\\host" normalise to it.
  if (path.startsWith('//') || path.includes('\\')) return null;
  if (NEVER_RETURN_TO.some(p => path === p || path.startsWith(`${p}?`))) return null;
  return path;
}

/** The dashboard a role lands on when there is no return path. */
export function dashboardFor(role) {
  if (role === 'student') return '/student/dashboard';
  if (role === 'recruiter') return '/recruiter/dashboard';
  if (role === 'admin') return '/admin/dashboard';
  return '/';
}

/** Where to send a freshly signed-in user: back where they came from, else home. */
export function postAuthDestination(role, next) {
  return safeNextPath(next) || dashboardFor(role);
}

export default safeNextPath;
